// POR QUÉ NO SE SUBIÓ UN ARCHIVO, dicho para quien lo está subiendo.
//
// Maigua (Johanna & Otto Arosemena), 2026-11-03: *"No me deja subir
// documentos ni elegibilidad"*. La pantalla solo decía **"1 archivo(s)
// fallaron"**: el `catch` contaba el fallo y tiraba la razón. Y donde sí se
// enseñaba, salía el texto del servidor en inglés ("Seat permission
// required"), que tampoco le dice a nadie qué hacer.
//
// Subir un archivo son tres pasos (`api.js#uploadDocument`), y cada uno falla
// por algo distinto y se arregla en un sitio distinto:
//   'permiso' → el servidor pide la llave para escribir (los asientos
//               necesitan "Subir / eliminar documentos"). Lo arregla el dueño.
//   'envio'   → el archivo no llegó al almacén de Azure: conexión, o una
//               red/antivirus que bloquea *.blob.core.windows.net.
//   'guardar' → llegó, pero no se pudo registrar (tamaño, cliente…).
// Por eso `uploadDocument` marca en el error el paso y el estado HTTP, y aquí
// se convierte en una frase. Elegibilidad sube por el MISMO camino, así que
// fallaba igual y con el mismo silencio.

/** El tamaño máximo que acepta el servidor (MAX_DOCUMENT_BYTES), para decirlo en MB. */
const mb = (n) => `${Math.round(Number(n) / 1048576)} MB`;

/**
 * La frase para un error de `uploadDocument`. Nunca devuelve vacío: si no se
 * reconoce el caso, va el mensaje original detrás de "Error al subir".
 */
export function motivoDeSubidaFallida(err, es = true) {
  const status = Number(err?.status) || 0;
  const codigo = String(err?.serverCode || err?.message || '');

  if (status === 403 || /permission|permis/i.test(codigo)) {
    return es
      ? 'Tu usuario no tiene permiso para subir documentos. El dueño de la agencia lo activa en Equipo → Permisos del asiento → Documentos y Firmas → "Subir / eliminar documentos".'
      : 'Your user is not allowed to upload documents. The agency owner can turn it on in Team → Seat permissions → Documents & Signatures → "Upload / delete documents".';
  }
  if (status === 401) {
    return es ? 'Tu sesión venció. Recarga la página, entra otra vez y vuelve a subirlo.' : 'Your session expired. Reload the page, sign in again and retry.';
  }
  if (status === 413 || /exceeds/i.test(codigo)) {
    const max = /(\d{6,})/.exec(codigo)?.[1];
    return es
      ? `El archivo es demasiado grande${max ? ` (máximo ${mb(max)})` : ''}. Comprímelo o divídelo y vuelve a subirlo.`
      : `The file is too large${max ? ` (max ${mb(max)})` : ''}. Compress or split it and retry.`;
  }
  if (err?.etapa === 'envio') {
    return es
      ? 'El archivo no llegó al almacén de documentos. Revisa la conexión a internet; si estás en una red de oficina o con antivirus, puede estar bloqueando "blob.core.windows.net".'
      : 'The file did not reach document storage. Check the internet connection; an office network or antivirus may be blocking "blob.core.windows.net".';
  }
  if (/storage not configured/i.test(codigo) || status >= 500) {
    return es
      ? 'El servidor de documentos no respondió. Inténtalo de nuevo en unos minutos; si sigue, avísanos por soporte.'
      : 'The document server did not respond. Try again in a few minutes; if it persists, contact support.';
  }
  const original = String(err?.message || '').trim();
  return (es ? 'Error al subir' : 'Upload failed') + (original ? `: ${original}` : '.');
}

/**
 * El aviso de una subida de VARIOS archivos: cuántos fallaron y por qué. Si
 * fallaron por razones distintas se da la primera; casi siempre es la misma.
 */
export function avisoDeArchivosFallidos(fallos, es = true) {
  if (!fallos.length) return '';
  const cuantos = es ? `${fallos.length} archivo(s) no se subieron` : `${fallos.length} file(s) were not uploaded`;
  return `${cuantos}. ${motivoDeSubidaFallida(fallos[0], es)}`;
}
